import React, { useState } from 'react';

import { Badge, Button, Col, Row, Spinner, Table } from 'react-bootstrap';
import { css, StyleSheet } from 'aphrodite';

import Container from 'components/shared/Container';
import { GLOBAL_VENDORS_FOR_DROPDOWN, UNBRANDED_GMV_PRODUCTS } from 'lib/networking/endpoints';
import LoadingSpinner from 'components/shared/LoadingSpinner';
import PaginationBar from 'components/shared/PaginationBar';
import SearchableScrollableSelect from 'components/shared/SearchableScrollableSelect';
import useGet from 'lib/hooks/useGet';

const PAGE_SIZE = 100;

function UnbrandedProductsView() {
  const [currentPage, setCurrentPage] = useState(0);
  const [selectedVendor, setSelectedVendor] = useState(null);

  const offset = currentPage * PAGE_SIZE;

  const { data, loading } = useGet(UNBRANDED_GMV_PRODUCTS, {
    limit: PAGE_SIZE,
    offset,
    global_vendor_id: selectedVendor?.value || null,
  });

  const { data: countData, loading: countLoading } = useGet(UNBRANDED_GMV_PRODUCTS, {
    global_vendor_id: selectedVendor?.value,
    count_only: true,
  });

  const totalCount = countData?.result?.[0]?.TOTAL_COUNT || 0;
  const totalPages = Math.ceil(totalCount / PAGE_SIZE);
  const products = data?.result || [];

  const handlePageChange = page => {
    setCurrentPage(page);
  };

  const getStatusBadge = status => {
    const variant =
      status === 'ACTIVE' ? 'success' : status === 'INACTIVE' ? 'secondary' : 'warning';
    return <Badge variant={variant}>{status || 'N/A'}</Badge>;
  };

  return (
    <Container className={css(styles.container)}>
      <div className="d-flex justify-content-between align-items-center">
        <h3 className="font-weight-bold">Unbranded Products</h3>
      </div>
      <Row>
        <Col md={3} className="mt-3">
          <SearchableScrollableSelect
            onChange={e => {
              setSelectedVendor(e);
              setCurrentPage(0);
            }}
            fetchUrl={GLOBAL_VENDORS_FOR_DROPDOWN}
            optionMapper={vendor => ({
              label: vendor.name,
              value: vendor.id,
            })}
            autoFocus={false}
            placeholder="Select Distributor"
          />
        </Col>
        <Col md={3} className="mt-4">
          {countLoading ? (
            <Spinner animation="border" role="status" size="sm" />
          ) : (
            <Badge variant="info">TOTAL: {totalCount.toLocaleString()}</Badge>
          )}
        </Col>
      </Row>
      <div className="d-flex justify-content-end mt-3">
        <PaginationBar
          totalPages={totalPages}
          currentPageNumber={currentPage}
          onPageChange={handlePageChange}
        />
      </div>
      {loading ? (
        <LoadingSpinner />
      ) : (
        <Table striped bordered hover className="mt-4">
          <thead>
            <tr>
              <th>SKU</th>
              <th>Name</th>
              <th>GTIN</th>
              <th>Pack Size</th>
              <th>Distributor</th>
              <th>Status</th>
              <th>Link</th>
            </tr>
          </thead>
          <tbody>
            {products.length === 0 ? (
              <tr>
                <td colSpan="7" className="text-center text-muted">
                  No unbranded products found
                </td>
              </tr>
            ) : (
              products.map(item => (
                <tr key={item.ID}>
                  <td>{item.VENDOR_SKU}</td>
                  <td>{item.NAME}</td>
                  <td>{item.GTIN || '-'}</td>
                  <td>{item.PACK_SIZE || '-'}</td>
                  <td>{item.GLOBAL_VENDOR_NAME}</td>
                  <td>{getStatusBadge(item.PRODUCT_STATUS)}</td>
                  <td>
                    <Button
                      variant="link"
                      onClick={() => {
                        window.open(
                          `/global-product-detail/${item.GLOBAL_PRODUCT_ID}/${item.ID}/distributor`,
                          '_blank',
                        );
                      }}
                    >
                      View
                    </Button>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </Table>
      )}
      <div className="d-flex justify-content-end mt-3">
        <PaginationBar
          totalPages={totalPages}
          currentPageNumber={currentPage}
          onPageChange={handlePageChange}
        />
      </div>
    </Container>
  );
}

const styles = StyleSheet.create({
  container: {
    minHeight: '150vh',
    overflowX: 'hidden',
  },
});

export default UnbrandedProductsView;
